import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { loadInvoiceSettlementByPayToken } from '@/hooks/loadInvoiceSettlement';
import {
  clearPortalPaymentPending,
  DEFAULT_SETTLEMENT_DELAYS_MS,
  invalidatePortalPaymentQueries,
  isInvoiceSettledPaid,
  PORTAL_SETTLEMENT_MAX_AGE_MS,
  readPortalPaymentPending,
  shouldContinuePortalSettlementPoll,
} from '@/lib/portalPaymentSettlement';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Bounded refetch after portal wallet checkout until invoices.status is paid. */
export function useReconcilePortalPaymentSettlement() {
  const qc = useQueryClient();

  useEffect(() => {
    const initial = readPortalPaymentPending();
    if (!initial) return;
    if (
      !shouldContinuePortalSettlementPoll({
        pending: initial,
        confirmedPaid: false,
        maxAgeMs: PORTAL_SETTLEMENT_MAX_AGE_MS,
      })
    ) {
      clearPortalPaymentPending();
      return;
    }

    let cancelled = false;

    async function reconcile() {
      for (let attempt = 0; attempt <= DEFAULT_SETTLEMENT_DELAYS_MS.length; attempt += 1) {
        if (cancelled) return;

        const pending = readPortalPaymentPending();
        if (!pending) return;
        if (
          !shouldContinuePortalSettlementPoll({
            pending,
            confirmedPaid: false,
            maxAgeMs: PORTAL_SETTLEMENT_MAX_AGE_MS,
          })
        ) {
          clearPortalPaymentPending();
          return;
        }

        const invoice = await loadInvoiceSettlementByPayToken(pending.payToken);
        if (cancelled) return;

        if (isInvoiceSettledPaid(invoice)) {
          await invalidatePortalPaymentQueries(qc);
          clearPortalPaymentPending();
          return;
        }

        if (attempt < DEFAULT_SETTLEMENT_DELAYS_MS.length) {
          await sleep(DEFAULT_SETTLEMENT_DELAYS_MS[attempt]);
          if (cancelled) return;
          await invalidatePortalPaymentQueries(qc);
        }
      }

      const leftover = readPortalPaymentPending();
      if (
        leftover &&
        !shouldContinuePortalSettlementPoll({
          pending: leftover,
          confirmedPaid: false,
        })
      ) {
        clearPortalPaymentPending();
      }
    }

    void reconcile();
    return () => {
      cancelled = true;
    };
  }, [qc]);
}
